import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useApp } from "@/contexts/app-context";
import { getTemplateCategories, getTemplates, Template, TemplateCategory } from "@/services/supabase/templates";
import { ToolCallMessagePartComponent, useThreadRuntime } from "@assistant-ui/react";
import { ArrowRight, LayoutTemplate, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const tryParseJSON = (jsonString: string) => {
    try {
        return JSON.parse(jsonString);
    } catch (e) {
        return null;
    }
};

export const GetTemplateCategoriesTool: ToolCallMessagePartComponent = ({
    argsText,
    result,
}) => {
    const threadRuntime = useThreadRuntime();
    const { projectId } = useApp();

    const [categories, setCategories] = useState<TemplateCategory[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [isFetchingData, setIsFetchingData] = useState(true);

    // Parse result
    const rawData = useMemo(() => typeof result === 'string' ? tryParseJSON(result) : result, [result]);
    const isLoadingResult = result === undefined && !rawData;

    const payloadData = rawData?.data || rawData;
    const suggestedEntityName = payloadData?.suggestedEntityName;

    useEffect(() => {
        let isMounted = true;

        async function loadData() {
            if (payloadData?.categories && payloadData?.templates) {
                if (isMounted) {
                    setCategories(payloadData.categories);
                    setTemplates(payloadData.templates);
                    setIsFetchingData(false);
                }
                return;
            }

            if (!projectId) {
                if (isMounted) setIsFetchingData(false);
                return;
            }
            try {
                setIsFetchingData(true);
                const [cats, tmpls] = await Promise.all([
                    getTemplateCategories(Number(projectId)),
                    getTemplates(Number(projectId))
                ]);

                if (isMounted) {
                    setCategories(cats);
                    setTemplates(tmpls);
                }
            } catch (error) {
                console.error("Failed to fetch templates:", error);
            } finally {
                if (isMounted) setIsFetchingData(false);
            }
        }

        loadData();
        return () => { isMounted = false; };
    }, [projectId, payloadData]);

    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

    const isLoading = isLoadingResult || isFetchingData;

    if (isLoading) {
        return (
            <Card className="w-full my-4 border-primary/20 bg-card/50">
                <CardContent className="flex items-center gap-4 p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                        <Loader2 className="h-5 w-5 text-primary animate-spin" />
                    </div>
                    <div className="space-y-1">
                        <p className="font-medium text-sm">Thinking...</p>
                        <p className="text-xs text-muted-foreground">Fetching templates and categories</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!categories.length && !templates.length) {
        return (
            <Card className="w-full my-4 border">
                <div className="p-4 text-center text-muted-foreground text-sm">
                    <p>Failed to load templates or categories.</p>
                </div>
            </Card>
        );
    }

    const handleSelectTemplate = () => {
        if (!selectedTemplateId) return;
        const template = templates.find((t) => String(t.id) === selectedTemplateId);
        if (!template) return;

        let messageText = `I want to create an entity named "${suggestedEntityName || 'New Entity'}" using template ID: ${template.id}.`;

        if (selectedCategoryId && selectedCategoryId !== "all") {
            messageText = `I want to create an entity named "${suggestedEntityName || 'New Entity'}" using template ID: ${template.id} and category ID: ${selectedCategoryId}.`;
        }

        threadRuntime.append({
            role: "user",
            content: [{ type: "text", text: messageText }]
        });
    };

    return (
        <Card className="w-full my-4 overflow-hidden border">
            <CardHeader className="bg-muted/30 pb-4 border-b">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
                        <LayoutTemplate className="h-4 w-4" />
                    </div>
                    <div>
                        <CardTitle className="text-sm font-semibold">Available Templates</CardTitle>
                        <CardDescription className="text-xs mt-0.5">
                            Select a category and template to continue
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                <div className="p-4 space-y-4">
                    {suggestedEntityName && (
                        <div className="bg-primary/5 p-3 rounded-md border border-primary/10 flex items-center gap-2">
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Suggested Entity Name:</span>
                            <Badge variant="outline" className="font-medium bg-background">{suggestedEntityName}</Badge>
                        </div>
                    )}

                    {categories.length > 0 && (
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Category</label>
                            <Select value={selectedCategoryId} onValueChange={(val) => {
                                setSelectedCategoryId(val);
                                setSelectedTemplateId(""); // Reset template selection when category changes
                            }}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select a category" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Categories</SelectItem>
                                    {categories.map((c) => (
                                        <SelectItem key={String(c.id)} value={String(c.id)}>
                                            {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Template</label>
                        <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId} disabled={templates.length === 0}>
                            <SelectTrigger className="w-full text-left">
                                <SelectValue placeholder={templates.length === 0 ? "No templates available" : "Select a template"} />
                            </SelectTrigger>
                            <SelectContent>
                                {templates.map((template) => {
                                    const category = categories.find((c) => c.id === template.category_id);
                                    return (
                                        <SelectItem key={String(template.id)} value={String(template.id)}>
                                            {template.name} {category && `(${category.name})`}
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="pt-2 flex justify-end">
                        <Button
                            className="w-full sm:w-auto gap-2"
                            disabled={!selectedTemplateId}
                            onClick={handleSelectTemplate}
                        >
                            Continue
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
